import { type KeyboardEvent, type ReactNode, useEffect, useRef } from 'react';

import { cn } from '@/design-system/lib/cn';

const KEY_STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

export type SegmentOption<T extends string> = { value: T; label: ReactNode; disabled?: boolean };

export type SegmentedControlProps<T extends string> = {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentOption<T>[];
  'aria-label': string;
  size?: 'md' | 'sm';
  /** Ocupa a largura toda, com os itens divididos igualmente. */
  block?: boolean;
  disabled?: boolean;
  className?: string;
};

/** Rola o trilho (só ele, não a página) até centralizar a opção ativa; nas pontas o navegador limita. */
function centerActiveOption(list: HTMLElement, immediate = false) {
  const option = list.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');
  if (!option || list.scrollWidth <= list.clientWidth) return;

  const box = list.getBoundingClientRect();
  const at = option.getBoundingClientRect();
  const left = list.scrollLeft + (at.left - box.left) - (box.width - at.width) / 2;
  const reduced = immediate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  list.scrollTo?.({ left, behavior: reduced ? 'auto' : 'smooth' });
}

/**
 * Escolha única entre poucas opções (sexo, escopo, recorrência, ordenação), como
 * `radiogroup`: setas movem e selecionam, só o item marcado entra no Tab.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  block = false,
  disabled = false,
  className,
  ...props
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const list = containerRef.current;
    if (!list) return;

    requestAnimationFrame(() => centerActiveOption(list, true));
    const handleResize = () => centerActiveOption(list, true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const list = containerRef.current;
    if (!list) return;
    centerActiveOption(list);
  }, [value]);

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const step = KEY_STEP[event.key] ?? 0;

    if (!step) return;

    event.preventDefault();
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    const pos = enabled.indexOf(index);
    const next = enabled[(pos + step + enabled.length) % enabled.length];

    if (next === undefined) return;

    onValueChange(options[next]!.value);
    refs.current[next]?.focus();
  };

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={props['aria-label']}
      className={cn(
        'scrollbar-none inline-flex max-w-full min-w-0 gap-0.5 overflow-x-auto rounded-control bg-line-input p-0.75',
        block && 'flex w-full',
        className
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        // Sem nada marcado (ainda não escolhido), o primeiro entra no Tab.
        const tabbable = selected || (index === 0 && !options.some((o) => o.value === value));

        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={tabbable ? 0 : -1}
            disabled={disabled || option.disabled}
            onClick={() => onValueChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control-sm px-3 font-medium whitespace-nowrap transition-colors disabled:opacity-50',
              '[&_svg]:size-3.5 [&_svg]:stroke-[1.8]',
              size === 'sm' ? 'h-7 text-meta' : 'h-8 text-small',
              block && 'flex-1',
              selected ? 'bg-white text-ink shadow-raised' : 'text-ink-subtle hover:text-ink'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
